<template>
  <div>
    <!-- Loading State -->
    <div v-if="loading && users.length === 0" class="text-center py-16">
      <Loader2 class="h-8 w-8 animate-spin mx-auto text-muted-foreground mb-4" />
      <p class="text-muted-foreground">Loading user directory...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="users.length === 0" class="text-center py-16">
      <div class="mx-auto w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
        <Users class="h-10 w-10 text-muted-foreground" />
      </div>
      <h2 class="font-semibold mb-2">No users found</h2>
      <p class="text-muted-foreground mb-6">Be the first to join the platform!</p>
      <Button @click="$router.push('/')"> Go to Home </Button>
    </div>

    <!-- User List -->
    <div v-else class="space-y-2">
      <UserListItem
        v-for="user in users"
        :key="user.id"
        :user="user"
        :show-bio="true"
        :is-liked="isLiked(user.id)"
        @view-profile="viewProfile"
        @toggle-like="toggleLike"
      />
    </div>

    <!-- Info Footer -->
    <div v-if="users.length > 0" class="text-center pt-6">
      <p class="text-sm text-muted-foreground px-4">
        <Info class="inline-block mr-1 h-4 w-4" />
        Tap a profile to view details. 🔥 indicates open to play, ❤️ shows previously liked users.
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../../stores/auth'
import { useUserStore } from '../../stores/user'
import { Users, Loader2, Info } from 'lucide-vue-next'
import Button from '../ui/Button.vue'
import UserListItem from '../UserListItem.vue'

const props = defineProps({
  excludeIds: {
    type: Array,
    default: () => []
  }
})

const router = useRouter()
const authStore = useAuthStore()
const userStore = useUserStore()

const loading = ref(false)
const allUsers = ref([])

// Helper function to check if a user is anonymous
function isAnonymousUser(user) {
  if (!user || typeof user !== 'object') return true

  const name = user.name

  return name === null || name === undefined || (typeof name === 'string' && name.trim() === '')
}

// Computed property to filter users
const users = computed(() => {
  try {
    if (!Array.isArray(allUsers.value)) {
      console.warn('Users data is not an array:', allUsers.value)
      return []
    }

    // Filter out anonymous users and malformed user objects
    const filteredUsers = allUsers.value.filter(user => {
      try {
        return !isAnonymousUser(user)
      } catch (error) {
        console.warn('Error checking user anonymity:', error, user)
        return false
      }
    })

    // Exclude provided IDs
    const excludeSet = new Set(props.excludeIds)

    return filteredUsers.filter(user => !excludeSet.has(user.id))
  } catch (error) {
    console.error('Error filtering users:', error)
    return []
  }
})

onMounted(async () => {
  if (authStore.user) {
    if (!userStore.profile) {
      await userStore.fetchProfile(authStore.user.id)
    }

    if (userStore.profile) {
      await userStore.fetchLikes()
    }
  }

  await refreshUsers()
})

async function refreshUsers() {
  loading.value = true

  try {
    const fetchedUsers = await userStore.fetchAllUsers()

    if (Array.isArray(fetchedUsers)) {
      allUsers.value = fetchedUsers
    } else {
      console.warn('fetchAllUsers returned non-array data:', fetchedUsers)
      allUsers.value = []
    }
  } catch (error) {
    console.error('Failed to refresh users:', error)
    allUsers.value = []
  } finally {
    loading.value = false
  }
}

function viewProfile(userId) {
  router.push(`/user/${userId}`)
}

async function toggleLike(userId) {
  if (!authStore.user || isOwnProfile(userId)) return

  try {
    await userStore.toggleLike(userId)
  } catch (error) {
    console.error('Failed to toggle like:', error)
  }
}

function isLiked(userId) {
  return userStore.likes.includes(userId)
}

function isOwnProfile(userId) {
  return authStore.user?.id === userId
}
</script>
